// Maps a raw API/GitHub error into a crisp, categorized notice for the UI. Keeps customers from
// ever seeing raw GitHub strings ("Resource not accessible by personal access token", "Bad
// credentials", …) — they get a clear title, a one-line explanation, and an actionable hint.

import { ApiError } from "./api";

export type ErrorTone = "error" | "warning";

export type DescribedError = {
  tone: ErrorTone;
  title: string;
  detail: string;
  hint?: string;
  action?: { label: string; href: string };
};

// Where the error happened — sets the fallback wording so an unmapped error still reads well.
export type GithubErrorContext =
  | "connect"
  | "repos"
  | "branches"
  | "link"
  | "import"
  | "push"
  | "unlink"
  | "disconnect"
  | "conflict"
  | "resolve";

const FALLBACK_TITLE: Record<GithubErrorContext, string> = {
  connect: "Couldn’t connect your GitHub account",
  repos: "Couldn’t load your repositories",
  branches: "Couldn’t load branches",
  link: "Couldn’t link this skill to GitHub",
  import: "Couldn’t import from GitHub",
  push: "Couldn’t push to GitHub",
  unlink: "Couldn’t unlink this skill",
  disconnect: "Couldn’t disconnect GitHub",
  conflict: "Couldn’t load the conflict",
  resolve: "Couldn’t resolve the conflict",
};

const RECONNECT = { label: "Reconnect in Settings", href: "/settings" };

function statusOf(e: unknown): number {
  return e instanceof ApiError ? e.status : -1;
}
function rawOf(e: unknown): string {
  return e instanceof Error ? e.message : typeof e === "string" ? e : "";
}
function has(haystack: string, ...needles: string[]): boolean {
  const h = haystack.toLowerCase();
  return needles.some((n) => h.includes(n.toLowerCase()));
}

/** Permission hint tuned to what the action needs from the token. */
function scopeHint(ctx: GithubErrorContext): string {
  if (ctx === "link" || ctx === "import") {
    return "Linking also registers a webhook, so the token needs Contents and Webhooks access " +
      "(classic PATs: the “repo” scope; fine-grained: Contents and Webhooks set to Read & write).";
  }
  if (ctx === "push" || ctx === "resolve") {
    return "Pushing needs write access to this repository (classic PATs: the “repo” scope; " +
      "fine-grained: Contents → Read & write).";
  }
  return "Your token doesn’t have access to this repository. Check its scopes, or that it can " +
    "see private repos if this one is private.";
}

export function describeGithubError(e: unknown, ctx: GithubErrorContext): DescribedError {
  const status = statusOf(e);
  const raw = rawOf(e);
  const fallback = FALLBACK_TITLE[ctx];

  // No HTTP status at all → couldn't even reach our own server.
  if (status === 0) {
    return {
      tone: "error",
      title: "Can’t reach SkillPatch",
      detail: "We couldn’t reach the server.",
      hint: "Check your internet connection and try again.",
    };
  }

  // GitHub itself was unreachable / errored (our backend maps transport + 5xx to 502).
  if (status === 502 || status === 503 || has(raw, "reach github", "couldn’t reach github", "couldnt reach github")) {
    return {
      tone: "warning",
      title: "GitHub is unreachable right now",
      detail: "We couldn’t reach GitHub to complete this.",
      hint: "This is usually temporary — wait a moment and try again.",
    };
  }

  // Rate limiting comes back as a 403 or 429 — check it before the permission/connect branches so
  // it isn't mistaken for a token problem.
  if (status === 429 || has(raw, "rate limit")) {
    return {
      tone: "warning",
      title: "GitHub rate limit reached",
      detail: "You’ve hit GitHub’s request limit for now.",
      hint: "Wait a minute or two and try again.",
    };
  }

  // Connect flow uses a freshly-pasted token, so frame a 401 as "this token is wrong" rather than
  // "your connection broke".
  if (ctx === "connect" && (status === 401 || status === 403 || has(raw, "bad credentials"))) {
    return {
      tone: "error",
      title: "That token didn’t work",
      detail: "GitHub didn’t accept this personal access token.",
      hint: "Check it was pasted correctly, hasn’t expired, and has the “repo” scope (or Contents " +
        "& Webhooks for a fine-grained token).",
    };
  }

  // Token rejected or no longer readable → the connection needs re-establishing.
  if (status === 401 || has(raw, "bad credentials", "token can no longer be read", "token can’t be read", "reconnect")) {
    return {
      tone: "error",
      title: "Your GitHub connection isn’t working",
      detail: "GitHub rejected the stored access token — it may have expired or been revoked.",
      hint: "Reconnect your GitHub account to restore syncing.",
      action: ctx === "connect" ? undefined : RECONNECT,
    };
  }

  if (status === 403) {
    return {
      tone: "error",
      title: "Insufficient GitHub permissions",
      detail: "Your token doesn’t have permission to do this on this repository.",
      hint: scopeHint(ctx),
      action: RECONNECT,
    };
  }

  if (status === 404) {
    return {
      tone: "error",
      title: "Repository or branch not found",
      detail: "We couldn’t find that repository or branch on GitHub.",
      hint: "Check the name is right and that your token can access it (private repos need the right scope).",
    };
  }

  // Stored token unreadable (backend returns 409 for TokenDecryptError).
  if (status === 409 && has(raw, "token", "read", "reconnect")) {
    return {
      tone: "error",
      title: "Your GitHub connection isn’t working",
      detail: "The stored GitHub token could not be read.",
      hint: "Reconnect your GitHub account to restore syncing.",
      action: ctx === "connect" ? undefined : RECONNECT,
    };
  }

  // Validation / conflict (422, or 409 not about the token) — GitHub's own message is specific
  // and safe to show, just framed cleanly.
  if (status === 422 || status === 409 || status === 400) {
    return {
      tone: "error",
      title: fallback,
      detail: raw || "The request was rejected.",
    };
  }

  // Anything else: a clean fallback title + whatever detail we have.
  return {
    tone: "error",
    title: fallback,
    detail: raw || "Something went wrong. Please try again.",
  };
}
